import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import * as workflowExecutionService from '../../services/workflowExecutionService';
import * as sessionService from '../../services/sessionService';
import { Spinner } from '../common/Spinner';
import { useTheme } from '../../contexts/ThemeContext';

interface RelatedExecutionsViewProps {
  workflowId: string;
  currentExecutionId?: string; // Optional - may not be present on workflow details page
  darkMode: boolean;
  compact?: boolean; // For more compact display on workflow details page
}

const RelatedExecutionsView: React.FC<RelatedExecutionsViewProps> = ({ 
  workflowId, 
  currentExecutionId,
  darkMode,
  compact = false
}) => {
  const [executions, setExecutions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortField, setSortField] = useState<string>('created_at');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [dateFilter, setDateFilter] = useState<number>(0); // 0: all, 1: today, 7: last week, 30: last month
  const navigate = useNavigate();

  useEffect(() => {
    const fetchExecutions = async () => {
      if (!workflowId) {
        console.error("getWorkflowExecutions called with empty workflowId");
        setError("No workflow ID available");
        setLoading(false);
        return;
      }
      
      try {
        console.log(`[WORKFLOW-REPLAY] Fetching executions for workflow ${workflowId}`);
        setLoading(true);
        const data = await workflowExecutionService.getWorkflowExecutions(workflowId);
        console.log(`[WORKFLOW-REPLAY] Received ${data.length} executions for workflow ${workflowId}:`, data);
        
        if (data.length === 0) {
          console.log(`[WORKFLOW-REPLAY] No executions found for workflow ${workflowId}`);
        } else {
          console.log(`[WORKFLOW-REPLAY] First execution:`, data[0]);
        }
        
        setExecutions(data);
        setError(null);
      } catch (err) {
        console.error(`[WORKFLOW-REPLAY] Error fetching executions for workflow ${workflowId}:`, err);
        setError("Failed to fetch related executions");
      } finally {
        setLoading(false);
      }
    };

    fetchExecutions();
  }, [workflowId]);

  const calculateDuration = (execution: any) => {
    if (!execution.created_at) return 'Unknown';
    
    const startTime = new Date(execution.created_at).getTime();
    const endTime = execution.end_time 
      ? new Date(execution.end_time).getTime() 
      : execution.updated_at 
        ? new Date(execution.updated_at).getTime()
        : Date.now();
    
    const durationMs = endTime - startTime;
    
    // Format duration
    if (durationMs < 60000) {
      return `${Math.round(durationMs / 1000)}s`;
    } else if (durationMs < 3600000) {
      return `${Math.round(durationMs / 60000)}m`;
    } else {
      const hours = Math.floor(durationMs / 3600000);
      const minutes = Math.round((durationMs % 3600000) / 60000);
      return `${hours}h ${minutes}m`;
    }
  };

  const handleSort = (field: string) => {
    if (field === sortField) {
      // Toggle direction if clicking the same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field and default to descending
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // Filter executions by status and date
  const filteredExecutions = executions.filter(execution => {
    // Status filter
    if (statusFilter !== 'all' && execution.status !== statusFilter) {
      return false;
    }
    
    // Date filter
    if (dateFilter > 0) {
      const executionDate = new Date(execution.created_at);
      const now = new Date();
      const cutoffDate = new Date();
      cutoffDate.setDate(now.getDate() - dateFilter);
      
      if (executionDate < cutoffDate) {
        return false;
      }
    }
    
    return true;
  });

  const sortedExecutions = [...filteredExecutions].sort((a, b) => {
    let comparison = 0;
    
    switch (sortField) {
      case 'id':
        comparison = a.id.localeCompare(b.id);
        break;
      case 'created_at':
        comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
        break;
      case 'status':
        comparison = a.status.localeCompare(b.status);
        break;
      case 'progress':
        const progressA = a.phases_count ? (a.phases_completed / a.phases_count) : 0;
        const progressB = b.phases_count ? (b.phases_completed / b.phases_count) : 0;
        comparison = progressA - progressB;
        break;
      case 'duration':
        // Fix the duration calculation for sorting
        const durationA = a.created_at ? (
          a.end_time 
            ? new Date(a.end_time).getTime() - new Date(a.created_at).getTime()
            : a.updated_at 
              ? new Date(a.updated_at).getTime() - new Date(a.created_at).getTime()
              : Date.now() - new Date(a.created_at).getTime()
        ) : 0;
      
        const durationB = b.created_at ? (
          b.end_time 
            ? new Date(b.end_time).getTime() - new Date(b.created_at).getTime()
            : b.updated_at 
              ? new Date(b.updated_at).getTime() - new Date(b.created_at).getTime()
              : Date.now() - new Date(b.created_at).getTime()
        ) : 0;
      
        comparison = durationA - durationB;
        break;
      default:
        comparison = 0;
    }
    
    return sortDirection === 'asc' ? comparison : -comparison;
  });

  // Helper function to get raw duration in ms for sorting
  const calculateDurationMs = (execution: any) => {
    if (!execution.created_at) return 0;
    
    const startTime = new Date(execution.created_at).getTime();
    const endTime = execution.end_time 
      ? new Date(execution.end_time).getTime() 
      : execution.updated_at 
        ? new Date(execution.updated_at).getTime()
        : Date.now();
    
    return endTime - startTime;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center p-4">
        <Spinner size={compact ? "sm" : "lg"} color={darkMode ? "text-blue-500" : "text-blue-600"} />
        <span className="ml-2">Loading executions...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`p-4 rounded-lg ${darkMode ? 'bg-red-900 text-red-200' : 'bg-red-100 text-red-800'}`}>
        Error: {error}
      </div>
    );
  }

  if (!executions.length) {
    return (
      <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
        No executions found for this workflow.
      </div>
    );
  }

  return (
    <div className={`rounded-lg shadow-md p-4 ${darkMode ? 'bg-gray-800 shadow-gray-900' : 'bg-white shadow-gray-200'}`}>
      <h3 className={`${compact ? 'text-md' : 'text-lg'} font-medium mb-4 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
        Workflow Replay ({executions.length})
      </h3>
      
      {/* Filters */}
      <div className="mb-4 flex flex-wrap gap-4">
        {/* Status filter */}
        <div>
          <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Filter by status:</span>
          <div className="flex flex-wrap gap-2 mt-1">
        <button
          className={`px-2 py-1 text-xs rounded-full ${
            statusFilter === 'all'
              ? darkMode ? 'bg-blue-600 text-white' : 'bg-blue-100 text-blue-800'
              : darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'
          }`}
          onClick={() => setStatusFilter('all')}
        >
          All
        </button>
        <button
          className={`px-2 py-1 text-xs rounded-full ${
            statusFilter === 'active'
              ? darkMode ? 'bg-blue-600 text-white' : 'bg-blue-100 text-blue-800'
              : darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'
          }`}
          onClick={() => setStatusFilter('active')}
        >
          Active
        </button>
        <button
          className={`px-2 py-1 text-xs rounded-full ${
            statusFilter === 'completed'
              ? darkMode ? 'bg-green-600 text-white' : 'bg-green-100 text-green-800'
              : darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'
          }`}
          onClick={() => setStatusFilter('completed')}
        >
          Completed
        </button>
        <button
          className={`px-2 py-1 text-xs rounded-full ${
            statusFilter === 'failed'
              ? darkMode ? 'bg-red-600 text-white' : 'bg-red-100 text-red-800'
              : darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'
          }`}
          onClick={() => setStatusFilter('failed')}
        >
          Failed
        </button>
            <button
              className={`px-2 py-1 text-xs rounded-full ${
                statusFilter === 'paused'
                  ? darkMode ? 'bg-yellow-600 text-white' : 'bg-yellow-100 text-yellow-800'
                  : darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'
              }`}
              onClick={() => setStatusFilter('paused')}
            >
              Paused
            </button>
          </div>
        </div>
        
        {/* Date filter */}
        <div>
          <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Filter by date:</span>
          <div className="flex flex-wrap gap-2 mt-1">
            <button
              className={`px-2 py-1 text-xs rounded-full ${
                dateFilter === 0
                  ? darkMode ? 'bg-blue-600 text-white' : 'bg-blue-100 text-blue-800'
                  : darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'
              }`}
              onClick={() => setDateFilter(0)}
            >
              All time
            </button>
            <button
              className={`px-2 py-1 text-xs rounded-full ${
                dateFilter === 1
                  ? darkMode ? 'bg-blue-600 text-white' : 'bg-blue-100 text-blue-800'
                  : darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'
              }`}
              onClick={() => setDateFilter(1)}
            >
              Today
            </button>
            <button
              className={`px-2 py-1 text-xs rounded-full ${
                dateFilter === 7
                  ? darkMode ? 'bg-blue-600 text-white' : 'bg-blue-100 text-blue-800'
                  : darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'
              }`}
              onClick={() => setDateFilter(7)}
            >
              Last 7 days
            </button>
            <button
              className={`px-2 py-1 text-xs rounded-full ${
                dateFilter === 30
                  ? darkMode ? 'bg-blue-600 text-white' : 'bg-blue-100 text-blue-800'
                  : darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'
              }`}
              onClick={() => setDateFilter(30)}
            >
              Last 30 days
            </button>
          </div>
        </div>
      </div>
      
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className={darkMode ? 'bg-gray-700' : 'bg-gray-50'}>
            <tr>
              <th 
                scope="col" 
                className={`px-4 py-2 text-left text-xs font-medium uppercase tracking-wider cursor-pointer ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}
                onClick={() => handleSort('id')}
              >
                Execution ID
                {sortField === 'id' && (
                  <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                )}
              </th>
              <th 
                scope="col" 
                className={`px-4 py-2 text-left text-xs font-medium uppercase tracking-wider cursor-pointer ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}
                onClick={() => handleSort('status')}
              >
                Status
                {sortField === 'status' && (
                  <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                )}
              </th>
              <th 
                scope="col" 
                className={`px-4 py-2 text-left text-xs font-medium uppercase tracking-wider cursor-pointer ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}
                onClick={() => handleSort('created_at')}
              >
                Created
                {sortField === 'created_at' && (
                  <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                )}
              </th>
              <th 
                scope="col" 
                className={`px-4 py-2 text-left text-xs font-medium uppercase tracking-wider cursor-pointer ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}
                onClick={() => handleSort('duration')}
              >
                Duration
                {sortField === 'duration' && (
                  <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                )}
              </th>
              <th 
                scope="col" 
                className={`px-4 py-2 text-left text-xs font-medium uppercase tracking-wider cursor-pointer ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}
                onClick={() => handleSort('progress')}
              >
                Progress
                {sortField === 'progress' && (
                  <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                )}
              </th>
              <th scope="col" className={`px-4 py-2 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Actions
              </th>
            </tr>
          </thead>
          <tbody className={`divide-y divide-gray-200 dark:divide-gray-700 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            {sortedExecutions.map((execution) => (
              <tr key={execution.id} className={execution.id === currentExecutionId ? 
                (darkMode ? 'bg-blue-900 bg-opacity-20' : 'bg-blue-50') : ''}>
                <td className="px-4 py-2 whitespace-nowrap text-sm">
                  <div className={`font-mono ${darkMode ? 'text-gray-300' : 'text-gray-800'}`}>
                    {execution.id.substring(0, 8)}...
                  </div>
                  {!compact && (
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      Session: {execution.session_id?.substring(0, 8) || 'N/A'}...
                    </div>
                  )}
                </td>
                <td className="px-4 py-2 whitespace-nowrap">
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                    ${execution.status === 'completed' 
                      ? (darkMode ? 'bg-green-900 text-green-200' : 'bg-green-100 text-green-800')
                      : execution.status === 'active'
                      ? (darkMode ? 'bg-blue-900 text-blue-200' : 'bg-blue-100 text-blue-800')
                      : execution.status === 'failed'
                      ? (darkMode ? 'bg-red-900 text-red-200' : 'bg-red-100 text-red-800')
                      : (darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800')
                    }`}>
                    {execution.status}
                  </span>
                </td>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  {new Date(execution.created_at).toLocaleString()}
                </td>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  {calculateDuration(execution)}
                </td>
                <td className="px-4 py-2 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                      <div 
                        className="bg-blue-600 h-2.5 rounded-full" 
                        style={{ width: `${execution.phases_count ? Math.round((execution.phases_completed / execution.phases_count) * 100) : 0}%` }}
                      />
                    </div>
                    <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                      {execution.phases_completed}/{execution.phases_count}
                    </span>
                  </div>
                </td>
                <td className="px-4 py-2 whitespace-nowrap text-sm">
                  {execution.id === currentExecutionId ? (
                    <span className="text-gray-500 dark:text-gray-400">Current</span>
                  ) : (
                    <div className="flex space-x-2">
                      <button
                        onClick={() => {
                          // Clear ALL execution caches before navigating
                          sessionService.invalidateExecutionCache();
                          navigate(`/workflows/executions/${execution.id}`);
                        }}
                        className={`px-3 py-1 rounded text-sm ${
                          darkMode 
                            ? 'bg-blue-700 hover:bg-blue-600 text-white' 
                            : 'bg-blue-100 hover:bg-blue-200 text-blue-800'
                        }`}
                      >
                        View
                      </button>
                      <button
                        onClick={async () => {
                          try {
                            const result = await workflowExecutionService.startWorkflowExecution(
                              execution.workflow_id,
                              execution.session_id
                            );
                            console.log('Execution cloned successfully:', result);
                            // Clear ALL execution caches before navigating
                            sessionService.invalidateExecutionCache();
                            navigate(`/workflows/executions/${result.id}`);
                          } catch (err) {
                            console.error("Failed to clone execution:", err);
                            alert(`Failed to clone execution: ${err.message || 'Unknown error'}`);
                          }
                        }}
                        className={`px-3 py-1 rounded text-sm ${
                          darkMode 
                            ? 'bg-green-700 hover:bg-green-600 text-white' 
                            : 'bg-green-100 hover:bg-green-200 text-green-800'
                        }`}
                        title="Clone this execution with the same session"
                      >
                        Clone
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default RelatedExecutionsView;
